/**
 * In-Kind Donations Admin Tab.
 *
 * List, search, filter, edit, download PDF, resend receipt, and CSV
 * export for physical-item donations. All dollar values shown here are
 * INTERNAL only — never printed on the donor receipt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Download, Send, FileText, Search, Edit3, Trash2, Loader2,
  RotateCcw, Mail,
} from 'lucide-react';
import RecordInKindDonationModal from '../../../components/admin/RecordInKindDonationModal';
import { formatDate, formatDateTime, tzAbbrev } from '../../../utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_LABEL = {
  new: 'New', like_new: 'Like New', good: 'Good', fair: 'Fair',
};

const fmtMoney = (n) => (n === null || n === undefined || n === '')
  ? '—'
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InKindDonationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/in-kind-donations`,
        { params, withCredentials: true },
      );
      setRows(r.data?.rows || []);
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.detail || 'Failed to load.' });
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const items = rows.reduce((s, r) => s + (r.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0), 0);
    const iev = rows.reduce((s, r) => s + (r.internal_estimated_value ? Number(r.internal_estimated_value) : 0), 0);
    return { count: rows.length, items, iev };
  }, [rows]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setModalOpen(true); };

  const downloadPdf = async (r) => {
    const url = `${BACKEND_URL}/api/admin/in-kind-donations/${r.id}/receipt.pdf`;
    window.open(url, '_blank', 'noopener');
  };

  const sendReceipt = async (r) => {
    let target = r.donor_email;
    if (!target) {
      // eslint-disable-next-line no-alert
      const entered = window.prompt('No email on file. Enter donor email to send receipt to:');
      if (!entered) return;
      target = entered.trim();
    }
    setBusyId(r.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/in-kind-donations/${r.id}/send-receipt`,
        r.donor_email ? {} : { email: target },
        { withCredentials: true },
      );
      setToast({ kind: 'ok', text: `Receipt emailed to ${target}` });
      load();
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.detail || 'Send failed' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Void in-kind donation ${r.receipt_number}?\n\nThis will archive the record with a full audit trail — the donation can be restored later by an admin and its history is never lost. Continue?`)) return;
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Optional: enter a short reason for voiding this record (e.g. "duplicate entry", "wrong donor", "test data"). Leave blank to skip.') || '';
    setBusyId(r.id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/in-kind-donations/${r.id}`,
        { withCredentials: true, params: reason ? { reason } : {} },
      );
      setToast({ kind: 'ok', text: `${r.receipt_number} voided. Audit entry recorded.` });
      load();
    } catch (e) {
      setToast({ kind: 'error', text: e?.response?.data?.detail || 'Archive failed' });
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const url = `${BACKEND_URL}/api/admin/in-kind-donations.csv?${params.toString()}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="space-y-4" data-testid="in-kind-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#0A0A0A]">In-Kind Donations</h2>
          <p className="text-xs text-[#4B5563] mt-0.5">
            Physical item contributions. Internal Estimated Value is
            admin-only and never appears on donor receipts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-[#0A0A0A] hover:bg-gray-50"
            data-testid="in-kind-export-csv"
          >
            <Download className="w-4 h-4" />Export CSV
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#BA1826] text-white rounded-lg text-sm font-semibold hover:bg-[#a01523]"
            data-testid="in-kind-record-btn"
          >
            <Plus className="w-4 h-4" />Record In-Kind Donation
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Records shown" value={totals.count} />
        <Tile label="Items received" value={totals.items} />
        <Tile label="Internal Est. Value (admin only)" value={fmtMoney(totals.iev)} muted />
        <Tile label="Time zone" value={tzAbbrev()} muted />
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#4B5563] font-medium">Search</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Donor, email, receipt #, item…"
              className="w-full border border-gray-300 rounded pl-8 pr-3 py-1.5 text-sm"
              data-testid="in-kind-search"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#4B5563] font-medium">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
            data-testid="in-kind-from"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#4B5563] font-medium">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
            data-testid="in-kind-to"
          />
        </div>
        <button
          onClick={() => { setQ(''); setDateFrom(''); setDateTo(''); }}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-[#4B5563] hover:text-[#0A0A0A]"
          data-testid="in-kind-clear-filters"
        >
          <RotateCcw className="w-3.5 h-3.5" />Clear
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[#4B5563] text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Receipt #</th>
                <th className="px-3 py-2 text-left">Donor</th>
                <th className="px-3 py-2 text-left">Date Received ({tzAbbrev()})</th>
                <th className="px-3 py-2 text-left">Items</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Internal Est. $</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-[#4B5563]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[#4B5563]" data-testid="in-kind-empty">
                  No in-kind donations recorded yet.
                </td></tr>
              )}
              {!loading && rows.map((r) => {
                const qty = (r.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0);
                const cats = (r.items || []).map((it) => it.category).filter(Boolean);
                const primary = cats[0] || '';
                const rest = cats.length > 1 ? ` +${cats.length - 1}` : '';
                return (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`in-kind-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.receipt_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#0A0A0A]">{r.donor_name || 'Anonymous'}</div>
                      <div className="text-xs text-[#4B5563]">{r.donor_email || (r.donor_phone || '—')}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.received_at)}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{primary}</span>{rest && <span className="text-[#4B5563]">{rest}</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{qty}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#4B5563]">{fmtMoney(r.internal_estimated_value)}</td>
                    <td className="px-3 py-2">
                      {r.status === 'receipt_sent' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium"
                          title={`Sent ${formatDateTime(r.receipt_sent_at)} to ${r.receipt_sent_to}${(r.receipt_send_history?.length || 0) > 1 ? ` · ${r.receipt_send_history.length} total sends` : ''}`}
                          data-testid={`ink-status-sent-${r.id}`}
                        >
                          <Mail className="w-3 h-3" />
                          Sent{(r.receipt_send_history?.length || 0) > 1 ? ` (${r.receipt_send_history.length}×)` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                          Recorded
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn testId={`ink-view-${r.id}`} title="View / Download PDF" onClick={() => downloadPdf(r)}><FileText className="w-4 h-4" /></IconBtn>
                        <IconBtn testId={`ink-send-${r.id}`} title={r.receipt_sent_at ? 'Resend receipt' : 'Send receipt'} onClick={() => sendReceipt(r)} busy={busyId === r.id}>
                          <Send className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn testId={`ink-edit-${r.id}`} title="Edit" onClick={() => openEdit(r)}><Edit3 className="w-4 h-4" /></IconBtn>
                        <IconBtn testId={`ink-del-${r.id}`} title="Archive" onClick={() => remove(r)} danger><Trash2 className="w-4 h-4" /></IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RecordInKindDonationModal
        open={modalOpen}
        initial={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={(saved) => {
          setToast({ kind: 'ok', text: `Saved ${saved?.receipt_number || 'in-kind donation'}.` });
          load();
        }}
      />

      {toast && (
        <div
          className={`fixed bottom-4 right-4 rounded-lg px-4 py-2 text-sm shadow-lg ${
            toast.kind === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
          }`}
          onClick={() => setToast(null)}
          data-testid="in-kind-toast"
        >{toast.text}</div>
      )}
    </div>
  );
}

const Tile = ({ label, value, muted }) => (
  <div className={`rounded-lg border p-3 ${muted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
    <div className="text-[10px] uppercase tracking-wide text-[#4B5563] font-medium">{label}</div>
    <div className={`mt-1 text-lg font-semibold ${muted ? 'text-[#4B5563]' : 'text-[#0A0A0A]'}`}>{value}</div>
  </div>
);

const IconBtn = ({ children, onClick, title, danger, busy, testId }) => (
  <button
    onClick={onClick}
    disabled={busy}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 ${
      danger ? 'text-red-600 hover:bg-red-50' : 'text-[#4B5563] hover:text-[#0A0A0A]'
    }`}
  >
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
  </button>
);
